import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { Field, useFormikContext } from 'formik';
import { decode } from 'html-entities';
import { useUpdateEffect } from 'react-use';
import qs from 'qs';

import { checklist } from 'assets';
import { useAxios } from 'configs/requests';
import { Error, Loading } from 'components/Fetch';
import { Search } from 'pages/Dashboard/Common/Filters';

import { IInvitation } from '../Onboard';

interface CarePlansProps {
  setCarePlans: any;
}

const CarePlans = ({ setCarePlans }: CarePlansProps) => {
  const intl = useIntl();
  const gid = useFormikContext<IInvitation>().values.gid;
  const [search, setSearch] = useState<string>('');

  const [{ data, loading, error }, refetch] = useAxios(
    {
      url: `/clinician/2.0.0/CarePlanTemplate?${qs.stringify({ group_id: gid, statuses: '1' })}`,
      method: 'GET'
    },
    { useCache: false }
  );

  const getCarePlanTitle = (carePlan: ICarePlanTemplate) => {
    return carePlan.template_type === 'PRIVATE'
      ? `${decode(carePlan.template_description)} - ${intl.formatMessage({
          id: 'organizationKey'
        })}`
      : intl.formatMessage({ id: carePlan.template_description }) || carePlan.template_description;
  };

  const sortedAndFilteredCarePlans = (data?.carePlanTemplates || [])
    .filter((carePlan: ICarePlanTemplate) => {
      if (!search) return true;
      const title = getCarePlanTitle(carePlan);
      return title.toLowerCase().includes(search.toLowerCase());
    })
    .sort((a: any, b: any) => {
      return getCarePlanTitle(a).localeCompare(getCarePlanTitle(b));
    });

  const handleSearch = (value?: string) => {
    if (!value || value === '') {
      setSearch('');
    } else {
      setSearch(value);
    }
  };

  useUpdateEffect(() => {
    if (data?.carePlanTemplates) {
      setCarePlans(data?.carePlanTemplates);
    }
  }, [data, setCarePlans]);

  return (
    <>
      <h4 className="mb-4" data-test="select-careplan-title">
        {intl.formatMessage({ id: 'carePlansKey' })}
      </h4>
      <div
        className="d-flex align-items-center p-4 bg-blue-light mb-4 br-5"
        data-test="select-careplan-disclaimer"
      >
        <img src={checklist} />
        <p className="m-0 ms-3">{intl.formatMessage({ id: 'selectCarePlansKey' })}</p>
      </div>
      {loading && <Loading />}
      {error && <Error retry={refetch} />}
      <Container fluid className="mb-3 p-0">
        <Row>
          <Col sm={12}>
            <Search
              placeholder={`${intl.formatMessage({ id: 'searchByKey' })} ${intl.formatMessage({
                id: 'titleKey'
              })}`}
              autoReset={true}
              autoSearch={true}
              handleSearch={handleSearch}
              handleReset={handleSearch}
              active={!!search}
              defaultValue={search}
              dataTestId="careplan-search"
            />
          </Col>
        </Row>
      </Container>
      <Row data-test="careplan-list">
        {sortedAndFilteredCarePlans.map((carePlan: ICarePlanTemplate) => {
          const label = getCarePlanTitle(carePlan);
          return (
            <Form.Group className="form-group" as={Col} md="6" key={carePlan.template_id}>
              <Form.Label className="bg-light d-flex p-3 cursor-pointer shadow-sm">
                <Field
                  type="checkbox"
                  name="careplanTemplateIds"
                  id={carePlan.template_id}
                  label={label}
                  value={carePlan.template_id}
                  as={Form.Check}
                  data-test={`careplan-${carePlan.template_id}`}
                />
              </Form.Label>
            </Form.Group>
          );
        })}
      </Row>
    </>
  );
};

export default CarePlans;
